import * as React from 'react'
import { images } from '../../../theme'
import { Link, RouteComponentProps, NavLink } from 'react-router-dom'
import { PHOTOS_STORE, WORKSPACES_STORE } from '../../../stores'
import { inject } from 'mobx-react'
import { PhotosStore } from '../../../stores/PhotosStore'
import { Bars3Icon as MenuIcon, ArrowUpTrayIcon as AddPhotoIcon } from '@heroicons/react/24/outline'
import { WorkspacesStore } from '../../../stores/WorkspaceStore'
import { API, IPerson, IWorkspace } from '../../../services/api'
import { useState, useRef, useCallback } from 'react'
import { SEARCH_QUERY_PARAM } from '../../../constants/config'
import AlbumModel from '../../../models/album'
import { useAlbumAutocomplete } from '../../AlbumAutocomplete/AlbumAutocomplete.hook'
import { useDebouncedOptions } from '../../../utils/useDebouncedOptions'
import { serializeGalleryFilters } from '../../FiltersDrawer'
import { SearchCombobox } from './SearchCombobox'

interface NavBarBaseProps extends RouteComponentProps {
  [PHOTOS_STORE]?: PhotosStore
  [WORKSPACES_STORE]?: WorkspacesStore
  workspaceSlug: string
  activeWorkspace?: IWorkspace
  toggleDrawer: (open: boolean) => void
  toggleWorkspaceDrawer: (open: boolean) => void
  onProfileClick: () => void
  initials: string
}

const NavBarBase: React.FC<NavBarBaseProps> = (props) => {
  const [searchValue, setSearchValue] = useState(() => {
    const params = new URLSearchParams(props.history.location.search)
    return params.get(SEARCH_QUERY_PARAM) || ''
  })
  // on mobile the expanded search takes the whole header row
  const [searchExpanded, setSearchExpanded] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  // Sync search value with URL query param when navigating
  React.useEffect(() => {
    const params = new URLSearchParams(props.location.search)
    const urlQuery = params.get(SEARCH_QUERY_PARAM) || ''
    setSearchValue(urlQuery)
  }, [props.location.pathname, props.location.search])

  // Album suggestions matching the typed phrase (debounced, server-side filtering)
  const { setQuery, options: albumSuggestions } = useAlbumAutocomplete(
    props.activeWorkspace?.id ?? ''
  )

  // Person suggestions, same pattern
  const workspaceId = props.activeWorkspace?.id ?? ''
  const searchPersons = useCallback(
    async (phrase: string): Promise<Array<IPerson>> => {
      if (!workspaceId) return []
      const response = await API.getInstance().getPersons({ workspaceId, phrase, count: 5 })
      return response.ok && response.data ? response.data : []
    },
    [workspaceId]
  )
  const { setQuery: setPersonQuery, options: personSuggestions } = useDebouncedOptions<IPerson>({
    search: searchPersons,
  })

  const startSearch = useCallback(() => {
    const value = searchValue.trim()
    if (value) {
      props.history.push(`/${props.workspaceSlug}?${SEARCH_QUERY_PARAM}=${encodeURIComponent(value)}`)
    }
    inputRef.current?.blur()
  }, [searchValue, props.workspaceSlug, props.history])

  const clearSearch = useCallback(() => {
    setSearchValue('')
    setQuery('')
    setPersonQuery('')
    // navigate only when a search is actually applied in the URL; dismissing
    // a half-typed phrase must not yank the user off the current page
    const params = new URLSearchParams(props.location.search)
    if (params.get(SEARCH_QUERY_PARAM)) {
      props.history.push(`/${props.workspaceSlug}`)
    }
    // collapse the expanded mobile header
    inputRef.current?.blur()
  }, [props.workspaceSlug, props.history, props.location.search, setQuery])

  const handleSearchValueChange = useCallback((value: string) => {
    setSearchValue(value)
    setQuery(value)
    setPersonQuery(value)
  }, [setQuery, setPersonQuery])

  const openAlbum = useCallback((album: AlbumModel) => {
    props.history.push(`/${props.workspaceSlug}/albums/${album.id}`)
    inputRef.current?.blur()
  }, [props.workspaceSlug, props.history])

  const openPerson = useCallback(
    (person: IPerson) => {
      props.history.push({
        pathname: `/${props.workspaceSlug}`,
        search: `?${serializeGalleryFilters({ personIds: [person.id] })}`,
      })
      inputRef.current?.blur()
    },
    [props.workspaceSlug, props.history]
  )

  const baseLinkClass = 'text-sm font-medium px-3 py-2 rounded-md transition-colors text-gray-600 hover:bg-gray-50 hover:text-gray-900'
  const activeLinkClass = 'text-sm font-medium px-3 py-2 rounded-md transition-colors bg-gray-100 text-gray-900'

  return (
    <header className="bg-white">
      {/* h-16 keeps the navbar exactly 64px — PageContent (pt-16) and the date scrubber rely on it */}
      <nav className="mx-auto flex h-16 items-center justify-between gap-x-2 px-4 lg:px-6" aria-label="Global">
        {/* Logo */}
        <Link
          to={`/${props.workspaceSlug}`}
          className={`flex-shrink-0 ${searchExpanded ? 'hidden md:block' : ''}`}
        >
          <img
            className="h-8 w-auto"
            src={images.logo}
            alt="Szukadło"
          />
        </Link>

        {/* Search input - always visible, flexible width */}
        <SearchCombobox
          searchValue={searchValue}
          onSearchValueChange={handleSearchValueChange}
          onSubmitSearch={startSearch}
          onClear={clearSearch}
          albums={albumSuggestions}
          onSelectAlbum={openAlbum}
          persons={personSuggestions}
          onSelectPerson={openPerson}
          inputRef={inputRef}
          onExpandChange={setSearchExpanded}
        />

        {/* Desktop Navigation - hidden on mobile */}
        <div className="hidden md:flex items-center gap-x-1">
          <NavLink
            exact
            to={`/${props.workspaceSlug}`}
            className={baseLinkClass}
            activeClassName={activeLinkClass}
          >
            Moje zdjęcia
          </NavLink>
          <NavLink
            to={`/${props.workspaceSlug}/albums`}
            className={baseLinkClass}
            activeClassName={activeLinkClass}
          >
            Albumy
          </NavLink>
          <NavLink
            to={`/${props.workspaceSlug}/timelines`}
            className={baseLinkClass}
            activeClassName={activeLinkClass}
          >
            Osi czasu
          </NavLink>
          <NavLink
            to={`/${props.workspaceSlug}/upload`}
            className={baseLinkClass}
            activeClassName={activeLinkClass}
          >
            Prześlij
          </NavLink>

          {/* Profile button */}
          <button
            type="button"
            onClick={props.onProfileClick}
            className={baseLinkClass}
          >
            Profil
          </button>

          {/* Workspace icon - opens workspace drawer */}
          <div className="relative ml-4 group">
            <button
              type="button"
              onClick={() => props.toggleWorkspaceDrawer(true)}
              className="flex items-center justify-center w-10 h-10 rounded-lg text-white text-sm font-bold hover:opacity-80 transition-opacity cursor-pointer"
              style={{ backgroundColor: props.activeWorkspace?.color || '#6366f1' }}
            >
              {(props.activeWorkspace?.name || 'M')[0].toUpperCase()}
            </button>
            <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity">
              {props.activeWorkspace?.name || 'Moja przestrzeń'}
            </div>
          </div>
        </div>

        {/* Mobile hamburger menu - hidden on desktop and while the search is expanded */}
        <div className={`${searchExpanded ? 'hidden' : 'flex'} md:hidden items-center gap-1`}>
          <Link
            to={`/${props.workspaceSlug}/upload`}
            className="inline-flex items-center justify-center rounded-md p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <span className="sr-only">Dodaj zdjęcia</span>
            <AddPhotoIcon className="h-6 w-6" aria-hidden="true" />
          </Link>
          <button
            type="button"
            onClick={() => props.toggleDrawer(true)}
            className="inline-flex items-center justify-center rounded-md p-2.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <span className="sr-only">Open main menu</span>
            <MenuIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>
    </header>
  )
}

export default inject(WORKSPACES_STORE)(inject(PHOTOS_STORE)(NavBarBase))
